import { ColDef, ColGroupDef } from 'ag-grid-community';

import { TranslateService } from '@ngx-translate/core';

import { AgDateRangeFilterComponent } from '@sensika/ag-grid-kit';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import { AgDateCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-date-cell-renderer/ag-date-cell-renderer.component';
import { NomObjectClass } from 'src/app/core/constants';
import { OBJECT_SETTINGS_ACCESS_LEVEL } from 'src/app/core/object-settings-access-level.constants';

/**
 * Canonical column identities of the audit trail (WAP-1639). They double as
 * the API row property names and as the keys of every backend filter / sort
 * map (SEN-5240), so a saved view preset, a request and a Solr field all speak
 * the same name. Never suffix them with the rendering variant (`.absolute`).
 */
export const AUDIT_TRAIL_COL_IDS = {
  owner: 'owner',
  timeCreated: 'time_created',
  createdBy: 'created_by_user',
  timeModified: 'time_modified',
  modifiedBy: 'modified_by_user',
} as const;

/**
 * Which product area the grid belongs to. Analytics grids open with only the
 * "modified" pair visible; administrative grids (Admin, Sysadmin, Source
 * Catalog, Connector Manager) show all four columns.
 */
export type AuditTrailArea = 'analytics' | 'admin';

/** A user reference as emitted by the API (`TransformerFacade::userRef`). */
export interface AuditTrailUserRef {
  id: number;
  name?: string;
}

export interface AuditTrailColumnOptions {
  area: AuditTrailArea;
  /**
   * Emit the Created By / Modified By columns. Default true. False for grids
   * whose rows carry no user references at all (SQL-backed stores, reviewed in
   * SEN-5241) - an always-empty column is worse than no column.
   */
  includeUsers?: boolean;
  /**
   * Opens the user editor for a clicked user cell (owner, created by,
   * modified by). Omitted: the cells render as plain, non-clickable users.
   */
  onUserClick?: (user: AuditTrailUserRef) => void;
  /**
   * Open with the whole group hidden; the user shows it from the column menu
   * or a view preset. For grids whose rows are configuration, not activity
   * (Proxies, WAP-1777), where the audit trail is a lookup, not a working column.
   */
  hidden?: boolean;
  /** Initial sort carried by one of the time columns (most grids open newest-first). */
  defaultSort?: {
    colId: 'time_created' | 'time_modified';
    direction: 'asc' | 'desc';
  };
}

function userColumnDef(
  translateService: TranslateService,
  colId: string,
  headerKey: string,
  onUserClick: ((user: AuditTrailUserRef) => void) | undefined,
  initialHide: boolean,
): ColDef {
  return {
    colId,
    field: colId,
    headerName: translateService.instant(headerKey),
    filter: 'agTextColumnFilter',
    // The repo's standard width band for name and absolute-time columns; without
    // a minWidth the fit-to-width pass squeezed the group under a pinned column.
    minWidth: 150,
    maxWidth: 190,
    sortable: true,
    initialHide,
    // The cell shows avatar + name and is capped at 190px; the tooltip carries the full name.
    tooltipValueGetter: (params) => params.value?.name ?? '',
    cellRenderer: AgDataObjectCellRendererComponent,
    cellRendererParams: {
      object: colId,
      clickAccessLevel: OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.USER],
      ...(onUserClick
        ? { onClick: (user: AuditTrailUserRef) => onUserClick(user) }
        : {}),
    },
    cellStyle: { overflow: 'visible' },
  };
}

function timeColumnDef(
  translateService: TranslateService,
  colId: string,
  headerKey: string,
  initialHide: boolean,
): ColDef {
  return {
    colId,
    // The row property is a TimeInfo object; AgDateCellRendererComponent reads
    // the value through `colDef.field`, so the field names the `.absolute` leaf
    // while the colId stays the bare property name.
    field: `${colId}.absolute`,
    headerName: translateService.instant(headerKey),
    filter: AgDateRangeFilterComponent,
    minWidth: 150,
    maxWidth: 190,
    sortable: true,
    initialHide,
    cellRenderer: AgDateCellRendererComponent,
  };
}

/**
 * The Owner column of a user-editable DataObject (workspace, dashboard, feed,
 * dimension, connector, rule). Place it immediately before the audit trail
 * group; it is not part of the group because not every audited object has an
 * owner.
 */
export function buildOwnerColumnDef(
  translateService: TranslateService,
  onUserClick?: (user: AuditTrailUserRef) => void,
): ColDef {
  return userColumnDef(
    translateService,
    AUDIT_TRAIL_COL_IDS.owner,
    'grid.column.owner',
    onUserClick,
    false,
  );
}

/**
 * The standard "Audit Trail" column group: Time Created, Created By, Time
 * Modified, Modified By - in that order, married so they move as one.
 */
export function buildAuditTrailColumnGroup(
  translateService: TranslateService,
  options: AuditTrailColumnOptions,
): ColGroupDef {
  const includeUsers = options.includeUsers ?? true;
  const hideCreated = options.area === 'analytics';
  const children: ColDef[] = [
    timeColumnDef(
      translateService,
      AUDIT_TRAIL_COL_IDS.timeCreated,
      'grid.column.time-created',
      hideCreated,
    ),
  ];
  if (includeUsers) {
    children.push(
      userColumnDef(
        translateService,
        AUDIT_TRAIL_COL_IDS.createdBy,
        'grid.column.created-by',
        options.onUserClick,
        hideCreated,
      ),
    );
  }
  children.push(
    timeColumnDef(
      translateService,
      AUDIT_TRAIL_COL_IDS.timeModified,
      'grid.column.time-modified',
      false,
    ),
  );
  if (includeUsers) {
    children.push(
      userColumnDef(
        translateService,
        AUDIT_TRAIL_COL_IDS.modifiedBy,
        'grid.column.modified-by',
        options.onUserClick,
        false,
      ),
    );
  }

  if (options.defaultSort) {
    const sorted = children.find((c) => c.colId === options.defaultSort.colId);
    if (sorted) {
      sorted.sort = options.defaultSort.direction;
    }
  }

  if (options.hidden) {
    children.forEach((child) => (child.initialHide = true));
  }

  return {
    headerName: translateService.instant('grid.column-group.audit-trail'),
    marryChildren: true,
    children,
  };
}
